/* eslint-disable @typescript-eslint/no-explicit-any */

import { type JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type RouteComponentProps,
  withRouter
} from 'react-router-dom';

import {
  Box,
  Button,
  Link,
  Typography
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import { Form, Formik } from 'formik';
import styled from 'styled-components';

import {
  Control,
  Dialog,
  FormCard,
  Header,
  SplashLoader
} from '../../components';
import { withAPIService } from '../../networking';
import { generalErrorDialog } from '../../shared/dialogConfigs';
import ErrorBoundary from '../../shared/ErrorBoundary';
import { languageSelection } from '../../shared/translationConfig/helper';
import { withStore } from '../../store';
import { openDialog } from '../../store/dialog';
import { logoutAndRedirectToLogin } from '../../utils/auth/logout';
import { CONTROL_TYPE } from '../../utils/constants';
import timer from '../../utils/timer';
import { VALIDATION } from '../../utils/validation';
import { logger } from '../logger';
import Template from '../Template';
import { PAGE_NAME } from './constant';


const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 850px;
  padding: var(--spacing-xl, 24px)
    var(--spacing-lg, 16px);
  gap: var(--padding-base, 8px);
  background-color: var(--brand-white);
  border-radius: var(--corner-radius-base, 8px);

  @media (max-width: 900px) {
    min-width: 100%;
  }
`;

function getInitialState(customer: any) {
  return {
    type: 'technicalQuestion',
    email: customer.contactPersonEmail,
    phone: '',
    message: ''
  };
}

interface HelpPageProps extends RouteComponentProps {
  store: any;
  apiService: any;
  dispatch: any;
}


function Component({
  history,
  store,
  apiService,
  dispatch
}: HelpPageProps): JSX.Element {
  useEffect(() => {
    logger.info(
      'Red Carpet Page Landing - Help Page'
    );
  }, []);

  const { t, i18n } = useTranslation();
  const { apiEvent, customer } = store;
  const { oktaAuth } = useOktaAuth();

  const { isSubmitting } = apiEvent;
  const { pathname } = history.location;

  /* ------------ language ------------ */
  useEffect(() => {
    const sessionLang = languageSelection();

    sessionLang === null
      ? i18n.changeLanguage(
          customer.languageCode
        )
      : i18n.changeLanguage(sessionLang);
  }, [i18n, customer]);

  /* ------------ API + timer ------------ */
  useEffect(() => {
    !customer &&
      apiService
        .getCustomer(dispatch)
        .catch(() => {});

    timer(
      () =>
        apiService
          .revokeToken()
          .then(() =>
            logoutAndRedirectToLogin(oktaAuth)
          ),
      30,
      'min'
    );
  }, [apiService, history, dispatch, customer, oktaAuth, i18n]);

  const handleSubmit = ({ values, isValid }: any) => {
    const {
      customer: { id = '' } = {}
    } = store;

    if (isValid) {
      const {
        contactFirstName,
        contactLastName
      } = customer;

      values = {
        ...values,
        contactFirstName,
        contactLastName,
        requestId: id
      };

      apiService
        .submitHelpRequest(values, dispatch)
        .catch(() => {
          dispatch(
            openDialog(generalErrorDialog())
          );
        })
       .then(() => (history as any).goBack());
    }
  };

  const disableSubmit = (
    form: any,
    isSubmitting: boolean
  ) => !form.isValid || isSubmitting;

  const emailInput = {
    name: 'email',
    label: t('support.contact.email.label'),
    validations: [{ type: VALIDATION.email }],
    disableAutoSave: true,
    placeholder: t('support.contact.email.placeholder')
  };

  const phoneInput = {
    name: 'phone',
    label: t('support.contact.phoneNumber.label'),
    type: CONTROL_TYPE.numberFormat,
    disableAutoSave: true,
    validations: [{ type: VALIDATION.phoneNumber }],
    placeholder: t('support.contact.phoneNumber.placeholder')
  };

  const messageInput = {
    name: 'message',
    label: t('support.contact.message.label'),
    type: CONTROL_TYPE.textarea,
    disableAutoSave: true,
    placeholder: t('support.contact.message.placeholder'),
    maxLength: '1000'
  };

  const isCustomerReady = Boolean(store.customer);

  return (
    <Template>
      {!isCustomerReady ? (
        <SplashLoader />
      ) : (
        <Wrapper>
          <ErrorBoundary componentTitle={PAGE_NAME}>
            <Header
              isOnlyUtility
              pathname={pathname}
            />

            {store.customer && (
              <ContentWrapper>
                <Formik
                  validateOnMount
                  initialValues={getInitialState(customer)}
                  onSubmit={handleSubmit}
                >
                  {(props: any) => (
                    <Form>
                      <FormCard
                        title={t('support.contact.header')}
                        image="/food/onboarding/assets/Contact.svg"
                      >
                        <Control required {...emailInput} />
                        <Control {...phoneInput} />
                        <Control {...messageInput} />
                      </FormCard>

                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="body1"
                          sx={{
                            display: 'inline-block',
                            whiteSpace: 'normal'
                          }}
                        >
                          {t('support.contact.footer.text')}
                          <Link
                            variant="body1"
                            href="https://www.cargill.com/page/privacy"
                            target="_blank"
                            sx={{ whiteSpace: 'nowrap', ml: '4px' }}
                          >
                            {t('support.contact.footer.linkText')}
                          </Link>
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 3,
                          pt: 2,
                          flexDirection: {
                            xs: 'column-reverse',
                            sm: 'row'
                          },
                          width: '100%'
                        }}
                      >
                        <Button
                          variant="outlined"
                          type="button"
                          onClick={() => (history as any).goBack()}
                        >
                          {t('buttons.cancel')}
                        </Button>

                        <Button
                          variant="contained"
                          disabled={disableSubmit(
                            props,
                            isSubmitting
                          )}
                          type="button"
                          onClick={() => handleSubmit(props)}
                        >
                          {t('buttons.sendMessage')}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </ContentWrapper>
            )}

            <Dialog />
          </ErrorBoundary>
        </Wrapper>
      )}
    </Template>
  );
}
const connectRouter = withRouter(Component);
const connectAPI = withAPIService(connectRouter);
const HelpPage = withStore(connectAPI);

export default HelpPage;